import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { submitDonationSchema, type DonationType } from "@passa-a-pagina/shared";
import { api, ApiError } from "../lib/api";

export function Donate() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [type, setType] = useState<DonationType>("books");
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    const parsed = submitDonationSchema.safeParse({
      email,
      type,
      quantity,
      message: message.trim() || undefined,
    });
    if (!parsed.success) {
      const fe = parsed.error.flatten().fieldErrors;
      setErrors({
        email: fe.email?.[0],
        quantity: fe.quantity?.[0],
        message: fe.message?.[0],
      });
      return;
    }
    setSubmitting(true);
    try {
      await api<{ id: string }>("/api/donations", { method: "POST", body: parsed.data });
      setDone(true);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t("common.error");
      setErrors({ form: msg });
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="container auth">
        <div className="surface auth__card">
          <h1>{t("donate.doneTitle")}</h1>
          <p>{t("donate.doneBody")}</p>
          <p>
            <Trans i18nKey="donate.donePoints" values={{ email }} />
          </p>
          <Link to="/" className="btn btn-primary">{t("donate.doneBack")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container auth">
      <div className="surface auth__card">
        <h1>{t("donate.title")}</h1>
        <p>{t("donate.intro")}</p>

        <form onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="d-email">{t("donate.emailLabel")}</label>
            <input
              id="d-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? "d-email-err" : undefined}
              disabled={submitting}
            />
            {errors.email && (
              <p id="d-email-err" className="field__error" role="alert">{errors.email}</p>
            )}
          </div>

          <div className="field">
            <label htmlFor="d-type">{t("donate.typeLabel")}</label>
            <select
              id="d-type"
              value={type}
              onChange={(e) => setType(e.target.value as DonationType)}
              disabled={submitting}
            >
              <option value="books">{t("donate.typeBooks")}</option>
              <option value="boxes">{t("donate.typeBoxes")}</option>
              <option value="bags">{t("donate.typeBags")}</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="d-qty">{t("donate.qtyLabel")}</label>
            <input
              id="d-qty"
              type="number"
              min={1}
              max={10000}
              required
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              aria-invalid={Boolean(errors.quantity)}
              aria-describedby={errors.quantity ? "d-qty-err" : undefined}
              disabled={submitting}
            />
            {errors.quantity && (
              <p id="d-qty-err" className="field__error" role="alert">{errors.quantity}</p>
            )}
          </div>

          <div className="field">
            <label htmlFor="d-msg">{t("donate.msgLabel")}</label>
            <textarea
              id="d-msg"
              rows={4}
              maxLength={2000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={submitting}
            />
          </div>

          {errors.form && (
            <p className="field__error" role="alert">{errors.form}</p>
          )}

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? t("donate.submitting") : t("donate.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
